import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects';

import { restClient } from 'config/store/configureStore';
import { fetchPage } from 'modules/app/sagas/resolveAll';
import type { SimpleRequestConfig } from 'modules/app/types';
import { simpleRequestsLoader } from 'modules/app/utils';
import type { ApiError, PageContent } from 'modules/global/generic.types';

import * as templatesActions from './actions';
import * as types from './actionTypes';
import type { DeleteTemplateAction, FetchTemplatesFOByDealerRequestAction, RedirectToCustomTemplateLinkAction } from './types/actions';
import type { Template } from './types/entities';

const simpleRequests: Record<string, SimpleRequestConfig> = {
  [types.FETCH_GROUP_CUSTOM_TEMPLATE_REQUEST]: {},
  [types.DELETE_TEMPLATE]: {
    callbacks: {
      success: (_, meta, action) => templatesActions.deleteTemplateSuccess((action as DeleteTemplateAction).payload!.templateId, meta),
      failure: (error, meta, action) => templatesActions.deleteTemplateFailure(error, (action as DeleteTemplateAction).payload!.templateId, meta),
    },
    notifications: {
      success: {
        id: 'page.fiche.notification.delete_template.success',
        defaultMessage: 'Template deleted',
      },
      failure: {
        id: 'page.fiche.notification.delete_template.failure',
        defaultMessage: "Can't delete the template",
      },
    },
  },
};

function* fetchTemplatesFOByDealerWorker(action: FetchTemplatesFOByDealerRequestAction) {
  const { request } = action.payload!;

  try {
    const data: PageContent<Template> = yield* fetchPage<Template>(request as AxiosRequestConfig);
    yield put(templatesActions.fetchTemplatesFOByDealerSuccess(data, action.meta));
  } catch (error) {
    yield put(templatesActions.fetchTemplatesFOByDealerFailure(error as ApiError, action.meta));
  }
}

function* redirectToCustomTemplateLink({ payload, meta }: RedirectToCustomTemplateLinkAction) {
  const { request, templateId } = payload!;
  try {
    const { data: url } = (yield call(() => restClient.request<string>(request as AxiosRequestConfig))) as AxiosResponse<string>;
    yield put(templatesActions.redirectToCustomTemplateLinkSuccess({ templateId, link: url }, meta));
    yield call(window.open, url, '_self');
  } catch (error) {
    yield put(templatesActions.redirectToCustomTemplateLinkFailure(templateId, error as ApiError, meta));
  }
}

export default function* templateSagas() {
  yield all(simpleRequestsLoader(simpleRequests));
  yield takeEvery(types.FETCH_TEMPLATES_FO_BY_DEALER_REQUEST, fetchTemplatesFOByDealerWorker);
  yield takeLatest(types.REDIRECT_TO_CUSTOM_TEMPLATE_LINK_REQUEST, redirectToCustomTemplateLink);
}
